import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AgricolaAddOn, AgricolaPlan, AgricolaPlanPriceRange, AgricolaProduct, AgricolaToastType } from '../../../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../../../services/agricola.service';
import { SisSelectOption } from '../../../../sis/sis-select/sis-select.component';

@Component({
  selector: 'app-agricola-premium-step',
  templateUrl: './agricola-premium-step.component.html',
  styleUrls: ['./agricola-premium-step.component.css'],
  standalone: false
})
export class AgricolaPremiumStepComponent implements OnInit, OnChanges, OnDestroy {

  @Input() group!: FormGroup;
  @Input() productCode = '';
  @Input() pepGroup!: FormGroup;
  @Input() selectedIndex: number = 0;
  @Input() steps: string[] = [];
  @Output() nextStep = new EventEmitter<number>();
  @Output() previousStep = new EventEmitter<void>();
  // Emitted when the agent picks a different assistance product from the boxes;
  // the wizard swaps step1.productSelection.product and re-validates so this step
  // reloads the new product's plans.
  @Output() productChange = new EventEmitter<string>();

  @ViewChild('stepper') stepper!: any;

  showErrorBanner = false;
  toastMessage: string = '';
  toastType: AgricolaToastType = 'error';
  showToast: boolean = false;
  loadingPlans = false;
  plansLoadedKey = 0;
  plansLoadFailed = false;
  plans: AgricolaPlan[] = [];
  coverageItems: { label: string; value: string }[] = [];
  isAssistance = false;
  addOns: AgricolaAddOn[] = [];
  selectedAddOnIds: number[] = [];
  loadingAddOns = false;
  addOnsLoadFailed = false;
  loadingCondicionado = false;

  ageRangeOptions: SisSelectOption[] = [];
  planOptions: SisSelectOption[] = [];

  // Static copy for the assistance products section header.

  private readonly subscriptions = new Subscription();
  private readonly addOnFetchRequests = new Subject<void>();

  constructor(
    private readonly agricolaService: AgricolaService
  ) {}

  get premiumData(): FormGroup {
    return this.group.get('premiumData') as FormGroup;
  }

  get selectedPlan(): AgricolaPlan | undefined {
    const selectedPlanCode = String(this.premiumData?.get('planOption')?.value || '');
    return this.plans.find(plan => plan.plan === selectedPlanCode || String(plan.id) === selectedPlanCode) || this.plans[0];
  }

  get selectedAgeRange(): AgricolaPlanPriceRange | undefined {
    const selectedRange = String(this.premiumData?.get('ageRange')?.value || '');
    return this.selectedPlan?.price_ranges?.find(range => range.value === selectedRange) || this.selectedPlan?.price_ranges?.[0];
  }

  get tableGridColumns(): string {
    const columns = Math.max(this.plans.length, 1);
    return `repeat(${columns}, minmax(160px, 240px))`;
  }

  get stepTitle(): string {
    return this.isAssistance ? 'Asistencias' : 'Seguros';
  }

  get stepDescription(): string {
    return this.isAssistance
      ? 'Selecciona el producto de asistencia que deseas ofrecer al cliente.'
      : 'Selecciona el seguro que deseas ofrecer al cliente.';
  }

  // El rango de edad solo se muestra donde realmente define la prima: los planes
  // de seguro cotizan por rango, los de asistencia por precio fijo del plan.
  get showAgeRange(): boolean {
    return !this.isAssistance;
  }

  // Productos del mismo tipo que el seleccionado, ofrecidos como chips de cambio
  // rápido en este paso (incluye el producto actual).
  get selectableProducts(): AgricolaProduct[] {
    return this.agricolaService.getProductsOfSameKind(this.isAssistance);
  }

  isActiveAssistanceProduct(product: AgricolaProduct): boolean {
    return String(product.id) === this.productCode || product.internal_product_code === this.productCode;
  }

  // getProductsOfSameKind() returns fresh object copies on every call, so without
  // trackBy the *ngFor identity diff would recreate the chip nodes (and replay
  // their selection animation) on every change-detection pass.
  trackByProductId(_index: number, product: AgricolaProduct): number {
    return product.id;
  }

  onSelectAssistanceProduct(product: AgricolaProduct): void {
    if (this.isActiveAssistanceProduct(product)) {
      return;
    }
    this.productChange.emit(String(product.id));
  }

  get selectedProduct(): AgricolaProduct | null {
    return this.agricolaService.getProductBySelection(this.productCode);
  }

  // A product only offers the wording when one is configured for it (on the product
  // itself or on one of its plans). There is no generic fallback document any more,
  // so showing the button without a configured wording would just 404 on click.
  get showCondicionado(): boolean {
    return this.selectedProduct?.has_condicionado === true;
  }

  get finalPremiumDisplay(): string {
    return this.premiumData.get('premiumResult')?.value || this.formatMoney(this.basePremium() + this.agricolaService.getAddOnsTotal());
  }

  ngOnInit(): void {
    this.isAssistance = this.agricolaService.isAssistanceProduct;
    this.subscriptions.add(this.premiumData.valueChanges.subscribe(() => {
      // Si el usuario corrige datos, escondemos el banner
      if (this.showErrorBanner) {
        this.showErrorBanner = false;
      }

      this.refreshCoverageItems();
    }));
    this.bindPremiumChangeRecalculation();
    this.bindAddOnFetches();
    this.loadPlans();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['productCode'] && !changes['productCode'].firstChange) {
      this.loadPlans();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.addOnFetchRequests.complete();
  }


  onContinue(stepper: any): void {
    this.premiumData.markAllAsTouched();

    if (this.premiumData.invalid || !this.premiumData.get('premiumResult')?.value) {
      this.showErrorBanner = true;
      return;
    }

    this.showErrorBanner = false;
    stepper.next();
  }

  onContinueClick(): void {
    this.premiumData.markAllAsTouched();

    if (this.premiumData.invalid || !this.premiumData.get('premiumResult')?.value) {
      this.showErrorBanner = true;
      this.showToastMessage('Por favor, completa todos los campos obligatorios y calcula la prima para continuar.', 'error');
      return;
    }

    this.showErrorBanner = false;
    // Emitir el índice del siguiente step para avanzar
    const nextIndex = this.selectedIndex + 1;
    this.nextStep.emit(nextIndex);
  }

  showToastMessage(message: string, type: AgricolaToastType = 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
  }

  hideToast(): void {
    this.showToast = false;
  }

  goBack(): void {
    // Emitir evento para que el componente padre maneje la navegación hacia atrás
    this.previousStep.emit();
  }

  goToStep(index: number): void {
    console.log('goToStep', index);
    if (index <= this.selectedIndex && this.stepper) {
      this.selectedIndex = index;
      this.stepper.selectedIndex = this.selectedIndex;
    }
  }

  cancel() {
    console.log('Cancelar wizard');
  }

  goToNextStep(event: any) {
    // Emitir el índice del siguiente step
    const nextIndex = this.selectedIndex + 1;
    this.nextStep.emit(nextIndex);
  }

  getPlanPrice(plan: AgricolaPlan): string {
    const selectedRange = String(this.premiumData?.get('ageRange')?.value || '');
    const range = plan.price_ranges?.find(item => item.value === selectedRange) || plan.price_ranges?.[0];
    return this.formatMoney(range?.price ?? plan.price);
  }

  retryLoadPlans(): void { this.loadPlans(); }

  retryLoadAddOns(): void {
    this.addOnFetchRequests.next();
  }

  getAddOnId(addOn: AgricolaAddOn): number {
    return addOn.plan;
  }

  isAddOnSelected(addOn: AgricolaAddOn): boolean {
    return this.selectedAddOnIds.includes(this.getAddOnId(addOn));
  }

  onToggleAddOn(id: number): void {
    const index = this.selectedAddOnIds.indexOf(id);
    if (index > -1) {
      this.selectedAddOnIds.splice(index, 1);
    } else {
      this.selectedAddOnIds.push(id);
    }

    this.selectedAddOnIds = [...this.selectedAddOnIds];
    this.syncSelectionAndTotal();
  }

  addOnPriceLabel(addOn: AgricolaAddOn): string {
    return `$ ${Number(addOn.price || 0).toFixed(2)} extra`;
  }

  // Opens the "condicionado" (policy wording) PDF through the authenticated proxy,
  // so the browser shows it from our domain and never the raw storage URL. The
  // selected plan travels along because a plan can carry its own wording.
  async openCondicionado(): Promise<void> {
    if (this.loadingCondicionado) {
      return;
    }
    this.loadingCondicionado = true;
    try {
      await this.agricolaService.viewCondicionado(this.productCode, this.selectedPlan?.id);
    } catch {
      this.showToastMessage('No se pudo abrir el condicionado. Inténtalo de nuevo.', 'error');
    } finally {
      this.loadingCondicionado = false;
    }
  }

  private loadPlans(): void {
    if (!this.productCode) {
      return;
    }

    this.plansLoadFailed = false;
    this.loadingPlans = true;
    const plansRequest = this.isAssistance
      ? this.agricolaService.getAssistancePlans(this.productCode)
      : this.agricolaService.getPlans(this.productCode);
    const subscription = plansRequest.subscribe({
      next: plans => {
        this.loadingPlans = false;
        this.plans = plans;
        this.planOptions = plans.map(plan => ({
          label: plan.description,
          value: String(plan.plan)
        }));
        this.ageRangeOptions = this.buildAgeRangeOptions(plans);
        this.plansLoadedKey++;
        this.selectInitialPlanData();
        this.refreshCoverageItems();
        this.addOnFetchRequests.next();
      },
      error: () => {
        this.loadingPlans = false;
        this.plans = [];
        this.planOptions = [];
        this.ageRangeOptions = [];
        this.coverageItems = [];
        this.addOns = [];
        this.selectedAddOnIds = [];
        this.plansLoadFailed = true;
        this.showToastMessage('No fue posible cargar los planes del producto.', 'error');
      }
    });

    this.subscriptions.add(subscription);
  }

  // Resets premiumData to its first-visit defaults every time plans load
  // (initial entry or a revisit after navigating back). No previous selection
  // is preserved, so the step always looks like the first time it's opened.
  private selectInitialPlanData(): void {
    const planControl = this.premiumData.get('planOption');
    const ageRangeControl = this.premiumData.get('ageRange');

    if (planControl && this.planOptions.length) {
      planControl.setValue(this.planOptions[0].value);
    }

    if (ageRangeControl && this.ageRangeOptions.length) {
      ageRangeControl.setValue(this.ageRangeOptions[0].value);
    }

    this.premiumData.get('paymentMethod')?.setValue('Mensual');

    this.premiumData.get('premiumResult')?.setValue('');
  }

  private bindPremiumChangeRecalculation(): void {
    const planControl = this.premiumData.get('planOption');
    const ageRangeControl = this.premiumData.get('ageRange');

    if (planControl) {
      this.subscriptions.add(planControl.valueChanges.subscribe(() => {
        this.selectedAddOnIds = [];
        this.agricolaService.setSelectedAddOns([]);
        this.recalcFinalPremiumIfCalculated();
        this.addOnFetchRequests.next();
      }));
    }

    if (ageRangeControl) {
      this.subscriptions.add(ageRangeControl.valueChanges.subscribe(() => {
        this.recalcFinalPremiumIfCalculated();
      }));
    }
  }

  private bindAddOnFetches(): void {
    this.subscriptions.add(this.addOnFetchRequests.pipe(
      switchMap(() => this.fetchAddOnsForSelectedPlan())
    ).subscribe(addOns => {
      this.addOns = addOns;
      // Ambos flujos calculan la prima al elegir plan; los seguros ya no tienen
      // botón "Calcular". Para seguros addOns siempre viene vacío, así que el
      // total queda en la prima base del rango de edad.
      this.syncSelectionAndTotal();
    }));
  }

  private fetchAddOnsForSelectedPlan() {
    if (!this.isAssistance || !this.selectedPlan) {
      this.loadingAddOns = false;
      this.addOnsLoadFailed = false;
      return of([]);
    }

    this.loadingAddOns = true;
    this.addOnsLoadFailed = false;

    return this.agricolaService.getAssistanceAddOns(this.selectedPlan.plan).pipe(
      catchError(() => {
        this.addOnsLoadFailed = true;
        this.showToastMessage('No fue posible cargar las coberturas adicionales.', 'error');
        return of([]);
      }),
      switchMap(addOns => {
        this.loadingAddOns = false;
        return of(addOns);
      })
    );
  }

  private syncSelectionAndTotal(): void {
    const selected = this.addOns.filter(addOn => this.selectedAddOnIds.includes(this.getAddOnId(addOn)));
    this.agricolaService.setSelectedAddOns(selected);
    this.recalcFinalPremium();
  }

  private recalcFinalPremiumIfCalculated(): void {
    if (this.premiumData.get('premiumResult')?.value || this.selectedAddOnIds.length) {
      this.recalcFinalPremium();
    }
  }

  private recalcFinalPremium(): void {
    const premium = this.basePremium() + this.agricolaService.getAddOnsTotal();
    this.premiumData.get('premiumResult')?.setValue(this.formatMoney(premium), { emitEvent: false });
  }

  private basePremium(): number {
    // Assistance ignores the age range (removed from this step) and prices off
    // the selected plan; insurance keeps the age-range based pricing.
    if (this.isAssistance) {
      return this.selectedPlan?.price ?? 0;
    }
    return this.selectedAgeRange?.price ?? this.selectedPlan?.price ?? 0;
  }

  private buildAgeRangeOptions(plans: AgricolaPlan[]): SisSelectOption[] {
    const ranges = new Map<string, string>();

    plans.forEach(plan => {
      (plan.price_ranges || []).forEach(range => {
        ranges.set(range.value, range.label);
      });
    });

    return Array.from(ranges.entries()).map(([value, label]) => ({ value, label }));
  }

  private refreshCoverageItems(): void {
    const plan = this.selectedPlan;

    if (!plan) {
      this.coverageItems = [];
      return;
    }

    const planDetails = plan.config?.plan_detail || [];
    if (planDetails.length) {
      this.coverageItems = planDetails.map(detail => ({
        label: detail.info,
        value: detail.detail
      }));
      return;
    }

    this.coverageItems = (plan.coverages || []).map(coverage => ({
      label: coverage.detail,
      value: coverage.info
    }));
  }

  private formatMoney(value: unknown): string {
    const amount = Number(value) || 0;
    return `$${amount.toFixed(2)}`;
  }
}
